import React, { useState, useEffect, useCallback } from "react";
import { Box, Tabs, Tab } from "@material-ui/core";
import { useTranslate } from "react-admin";
import "./GroupMembersSelector.css";
import { GroupDto } from "../../../common/data/service-proxies";
import { useForm } from "react-final-form";
import { MemberSelector } from "./MemberSelector";
import { SelectedMembersDisplay } from "./SelectedMembersDisplay";
import resourceNames from "../../../common/data/resourceNames";
import {
  SELECTED_DRIVERS_UPDATED,
  SELECTED_VEHICLES_UPDATED,
  ISelectedDriverOrVehicle,
  GroupMembersContext,
  IGroupMembersContext,
  IGroupMembersAction,
} from "./context";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";

export enum DriverOrVehicle {
  Driver = "Driver",
  Vehicle = "Vehicle",
}

interface GroupMembersSelectorProps {
  record?: GroupDto;
  state: IGroupMembersContext;
  dispatch: React.Dispatch<IGroupMembersAction>;
  disabled?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      <Box>{children}</Box>
    </div>
  );
};

export const GroupMembersSelector = ({
  state,
  dispatch,
  disabled,
  ...props
}: GroupMembersSelectorProps) => {
  const form = useForm();
  const translate = useTranslate();
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    form.registerField("driverIds", () => {}, { value: true });
    form.registerField("vehicleIds", () => {}, { value: true });
  }, [form]);

  useEffect(() => {
    if (props.record?.drivers) {
      const payload = props.record.drivers.map((x) => ({
        id: x.id!,
        name: x.fullName!,
        status: x.status!,
      }));

      dispatch({
        type: SELECTED_DRIVERS_UPDATED,
        payload,
      });
    }

    if (props.record?.vehicles) {
      const payload = props.record.vehicles.map((x) => ({
        id: x.id!,
        name: x.name!,
        status: x.status!,
      }));

      dispatch({
        type: SELECTED_VEHICLES_UPDATED,
        payload,
      });
    }
  }, [props.record, dispatch]);

  const addMember = useCallback(
    (member: ISelectedDriverOrVehicle, driverOrVehicle: DriverOrVehicle) => {
      const formFieldName =
        driverOrVehicle === DriverOrVehicle.Driver ? "driverIds" : "vehicleIds";
      const stateFieldName =
        driverOrVehicle === DriverOrVehicle.Driver
          ? "selectedDrivers"
          : "selectedVehicles";
      const currentValue =
        (form.getFieldState(formFieldName)?.value as Array<number>) ??
        ([] as Array<number>);
      form.change(`${formFieldName}[${currentValue.length}]`, member.id);
      dispatch({
        type:
          driverOrVehicle === DriverOrVehicle.Driver
            ? SELECTED_DRIVERS_UPDATED
            : SELECTED_VEHICLES_UPDATED,
        payload: [...state[stateFieldName], member],
      });
    },
    [form, state, dispatch]
  );

  const removeMember = useCallback(
    (member: ISelectedDriverOrVehicle, driverOrVehicle: DriverOrVehicle) => {
      const stateFieldName =
        driverOrVehicle === DriverOrVehicle.Driver
          ? "selectedDrivers"
          : "selectedVehicles";
      const formFieldName =
        driverOrVehicle === DriverOrVehicle.Driver ? "driverIds" : "vehicleIds";
      const currentValue = (
        (form.getFieldState(formFieldName)?.value as Array<number>) ??
        ([] as Array<number>)
      ).findIndex((x) => x === member.id);
      form.mutators.remove(`${formFieldName}]`, currentValue);
      dispatch({
        type:
          driverOrVehicle === DriverOrVehicle.Driver
            ? SELECTED_DRIVERS_UPDATED
            : SELECTED_VEHICLES_UPDATED,
        payload: [...state[stateFieldName].filter((x) => x.id !== member.id)],
      });
    },
    [state, dispatch, form]
  );

  return (
    <GroupMembersContext.Provider value={state}>
      <div className="group-members">
        <Tabs
          className="group-members-tabs"
          value={currentTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_: React.ChangeEvent<{}>, newValue: number) =>
            setCurrentTab(newValue)
          }>
          <Tab label={translate("resources.admin-groups.labels.drivers")} />
          <Tab label={translate("resources.admin-groups.labels.vehicles")} />
        </Tabs>
        <TabPanel value={currentTab} index={0}>
          <Box className="group-members-selector">
            <ProtectedElement source="driverIds">
              <MemberSelector
                addMember={(driver) =>
                  addMember(driver, DriverOrVehicle.Driver)
                }
                dtoNameField="driverName"
                dtoPrefixField="driverId"
                resourceName={resourceNames.ADMIN_DRIVERS_VIEW}
                contextField="selectedDrivers"
                title={translate("resources.admin-groups.labels.drivers")}
                disabled={disabled}
              />
            </ProtectedElement>
            <ProtectedElement source="driverIds">
              <SelectedMembersDisplay
                disabled={disabled}
                source="driverIds"
                contextField="selectedDrivers"
                title={translate(
                  "resources.admin-groups.labels.selectedDrivers"
                )}
                removeMember={(driver) =>
                  removeMember(driver, DriverOrVehicle.Driver)
                }
                {...props}
              />
            </ProtectedElement>
          </Box>
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <Box className="group-members-selector">
            <ProtectedElement source="vehicleIds">
              <MemberSelector
                addMember={(vehicle) =>
                  addMember(vehicle, DriverOrVehicle.Vehicle)
                }
                dtoNameField="name"
                resourceName={resourceNames.ADMIN_VEHICLES_VIEW}
                contextField="selectedVehicles"
                title={translate("resources.admin-groups.labels.vehicles")}
              />
            </ProtectedElement>
            <ProtectedElement source="vehicleIds">
              <SelectedMembersDisplay
                source="vehicleIds"
                contextField="selectedVehicles"
                title={translate(
                  "resources.admin-groups.labels.selectedVehicles"
                )}
                removeMember={(vehicle) =>
                  removeMember(vehicle, DriverOrVehicle.Vehicle)
                }
                {...props}
              />
            </ProtectedElement>
          </Box>
        </TabPanel>
      </div>
    </GroupMembersContext.Provider>
  );
};
